import React from 'react';
import {Box, Typography, Grid} from '@mui/material';
import ClassDashboardShell from '../components/ClassDashboard.comp';
import NoDataAvailable from '../../../components/NoDataAvailable';
import CircularLoader from '../../../components/CircularLoader';
import {useDashboard} from '../../../context/DashboardContext';
import {SUPPRESSION_MIN_N} from '../../../utils/suppression';
import {BAND_COLOR} from '../constants';

import {ParticipationBuildupChart} from '../../DashboardCommon/ParticipationBuildupChart.comp';
const NAVY   = '#052F44';
const ORANGE = '#FF6900';

const KpiCard = ({label, value, sub, chipColor}) => (
    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%', textAlign: 'center'}}>
        <Typography sx={{fontSize: '11px', fontWeight: 700, color: ORANGE, letterSpacing: '1px', textTransform: 'uppercase', mb: 1.5}}>
            {label}
        </Typography>
        <Typography sx={{fontSize: '40px', fontWeight: 800, color: NAVY, lineHeight: 1, mb: 1}}>
            {value}
        </Typography>
        {chipColor ? (
            <Box sx={{
                display: 'inline-block', backgroundColor: chipColor, color: '#ffffff',
                fontSize: '12px', fontWeight: 700, px: 1.5, py: 0.4, borderRadius: '20px',
            }}>
                {sub}
            </Box>
        ) : (
            <Typography sx={{fontSize: '13px', color: '#6A7282'}}>{sub}</Typography>
        )}
    </Box>
);

const GuidanceBox = ({text}) => (
    <Box sx={{
        border: '1px solid #C7D9E8', borderLeft: `4px solid ${NAVY}`, borderRadius: '8px',
        backgroundColor: 'var(--brand-surface-subtle, #F0F6FB)', p: 2.5,
    }}>
        <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, mb: 1}}>
            Decision guidance:
        </Typography>
        <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>
            {text}
        </Typography>
    </Box>
);

const PARTICIPATION_SECTION_CODE = 'S_5';
const CARD_SECTIONS = [PARTICIPATION_SECTION_CODE];

const ParticipationSection = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTIONS });

    if (loading) {
        return (
            <ClassDashboardShell>
                <CircularLoader />
            </ClassDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <ClassDashboardShell>
                <NoDataAvailable />
            </ClassDashboardShell>
        );
    }

    // Defensively locate section S_5 data
    let sectionData = null;
    if (dashboardData.section_code === PARTICIPATION_SECTION_CODE) {
        sectionData = dashboardData;
    } else if (dashboardData.cards?.sections) {
        sectionData = dashboardData.cards.sections.find(s => s.section_code === PARTICIPATION_SECTION_CODE);
    } else if (Array.isArray(dashboardData.sections)) {
        sectionData = dashboardData.sections.find(s => s.section_code === PARTICIPATION_SECTION_CODE);
    } else if (dashboardData.cards && Array.isArray(dashboardData.cards)) {
        sectionData = dashboardData;
    }

    if (!sectionData) {
        return (
            <ClassDashboardShell>
                <NoDataAvailable />
            </ClassDashboardShell>
        );
    }

    const cards = sectionData?.cards || [];

    // C_1: TitleCard
    const titleCard = cards.find(c => c.card_type === 'TitleCard') || {};
    const titleText = titleCard.title || 'Participation Overview';
    const subTitleText = titleCard.footer || 'Has enough of my class participated to interpret the results?';

    // C_2: KPICard (PARTICIPATION)
    const c2 = cards.find(c => c.card_type === 'KPICard' && c.kpi_name?.toUpperCase() === 'PARTICIPATION') || {};
    
    // C_3: KPICard (PUPILS ASSESSED)
    const c3 = cards.find(c => c.card_type === 'KPICard' && c.kpi_name?.toUpperCase() === 'STUDENTS ASSESSED') || {};

    // C_4: TrendLineCard (Participation build-up)
    const trendCard = cards.find(c => c.card_type === 'TrendLineCard') || {};
    const thresholdVal = trendCard.threshold?.value ?? 50; // Data Map CD-14-04: 50 % threshold
    const thresholdLabel = trendCard.threshold?.label ?? '50% threshold';

    // Process KPI Cards
    const participationVal = c2.kpi_value ?? 0;
    const isBelow = participationVal < thresholdVal;
    const participationBand = c2.kpi_band || c2.kpi_data?.band || (isBelow ? 'Low' : 'High');

    const kpiData = [
        {
            label: c2.kpi_name || 'Participation',
            value: `${participationVal}%`,
            sub: participationBand,
            chipColor: BAND_COLOR[participationBand] || (isBelow ? BAND_COLOR.Low : BAND_COLOR.High)
        },
        {
            label: c3.kpi_name || 'Pupils Assessed',
            value: c3.kpi_value ?? '0',
            sub: c3.kpi_data ? `of ${c3.kpi_data.total}` : ''
        },
        {
            label: 'Threshold',
            value: `${thresholdVal}%`,
            sub: 'minimum'
        }
    ];

    // The build-up chart counts pupils (bars = new in the month, line = running total); the 50 % threshold
    // becomes the matching number of pupils out of the class size the card sends as y_max.
    const classSize = Number(trendCard.y_max) || 0;
    const thresholdPupils = classSize ? Math.round((thresholdVal / 100) * classSize) : null;

    // Dynamic Decision Guidance Text
    const total = c3.kpi_data?.total ?? 0;
    const participants = c3.kpi_data?.participants ?? 0;
    const missing = Math.max(0, total - participants);

    // Meeting the % threshold isn't enough on its own — a tiny class can hit
    // 50% with just 1 pupil assessed, which is still too few to generalize
    // from. The floor is the platform's small-group floor: under it the
    // datahouse withholds the class's scores altogether.
    const tooFewAssessed = participants > 0 && participants < SUPPRESSION_MIN_N;

    const guidanceText = isBelow
        ? `At ${participationVal}% the class is below the ${thresholdVal}% threshold, so treat its scores as provisional. Chase the ${missing} missing pupils before drawing firm conclusions — do not over-interpret a low-participation class.`
        : tooFewAssessed
            ? `At ${participationVal}% the class has met the ${thresholdVal}% threshold, but only ${participants} pupil${participants === 1 ? '' : 's'} ${participants === 1 ? 'was' : 'were'} assessed — too few to treat as representative of the whole class, and under ${SUPPRESSION_MIN_N} the class's scores are not shown. Assess more pupils before drawing conclusions.`
            : `At ${participationVal}% the class has met the ${thresholdVal}% threshold. The results are representative and can be trusted for decision-making.`;

    return (
        <ClassDashboardShell>
            <Box sx={{mb: 3}}>
                <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, lineHeight: 1.2, mb: 0.3}}>
                    {titleText}
                </Typography>
                <Typography sx={{fontSize: '14px', color: '#4B5563', fontStyle: 'italic', mb: 0.8}}>
                    {subTitleText}
                </Typography>
            </Box>

            <Grid container spacing={2.5} sx={{mb: 3}}>
                {kpiData.map((k) => (
                    <Grid item xs={12} sm={4} key={k.label}>
                        <KpiCard {...k}/>
                    </Grid>
                ))}
            </Grid>

            <ParticipationBuildupChart
                card={trendCard}
                title={trendCard.title || 'Coverage Build-Up Through the Cycle'}
                yMax={classSize || undefined}
                threshold={thresholdPupils !== null ? {value: thresholdPupils, label: `${thresholdLabel} (${thresholdPupils} pupils)`} : undefined}
                emptyText="No pupil has been assessed yet."
                sx={{mb: 3}}
            />

            <GuidanceBox text={guidanceText}/>
        </ClassDashboardShell>
    );
};

export default ParticipationSection;
